// src/data/scents.js
// Shopify Storefront API queries and static site content.
// All product queries use the @inContext directive so Shopify
// automatically returns prices in the user's local currency.

import { shopifyFetch } from '../lib/shopify';
import { GIFT_BAG_HANDLE } from '../context/CartContext';

const PRODUCTS_QUERY = `
  query GetProducts($country: CountryCode!) @inContext(country: $country) {
    products(first: 20) {
      edges {
        node {
          id
          title
          description
          handle
          tags
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 1) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 1) {
            edges {
              node {
                id
                availableForSale
              }
            }
          }
        }
      }
    }
  }
`;

const SINGLE_PRODUCT_QUERY = `
  query GetProduct($handle: String!, $country: CountryCode!) @inContext(country: $country) {
    product(handle: $handle) {
      id
      title
      description
      descriptionHtml
      seo {
        title
        description
      }
      handle
      tags
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 4) {
        edges {
          node {
            url
            altText
          }
        }
      }
      variants(first: 1) {
        edges {
          node {
            id
            availableForSale
            price {
              amount
              currencyCode
            }
          }
        }
      }
      metafields(identifiers: [
        { namespace: "custom", key: "notes" }
        { namespace: "custom", key: "top_notes" }
        { namespace: "custom", key: "heart_notes" }
        { namespace: "custom", key: "base_notes" }
      ]) {
        key
        value
      }
    }
  }
`;

export async function getScents(country = 'IE') {
    const data = await shopifyFetch(PRODUCTS_QUERY, { country });
    // The gift bag is only sold as the cart drawer add-on, never as a product
    return data.products.edges.filter(({ node }) => node.handle !== GIFT_BAG_HANDLE).map(({ node }) => ({
        id: node.id,
        name: node.title,
        description: node.description,
        handle: node.handle,
        tags: node.tags,
        price: parseFloat(node.priceRange.minVariantPrice.amount),
        currency: node.priceRange.minVariantPrice.currencyCode,
        image: node.images.edges[0]?.node.url || '/images/product-default.png',
        imageAlt: node.images.edges[0]?.node.altText || node.title,
        variantId: node.variants.edges[0]?.node.id,
        availableForSale: node.variants.edges[0]?.node.availableForSale,
    }));
}

export async function getScent(handle, country = 'IE') {
    const data = await shopifyFetch(SINGLE_PRODUCT_QUERY, { handle, country });
    const node = data.product;
    if (!node) return null;

    const metafields = node.metafields || [];
    const notes = metafields.find(m => m?.key === 'notes')?.value || null;
    const parseNoteList = (key) => {
        const raw = metafields.find(m => m?.key === key)?.value;
        if (!raw) return [];
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };
    const topNotes = parseNoteList('top_notes');
    const heartNotes = parseNoteList('heart_notes');
    const baseNotes = parseNoteList('base_notes');

    return {
        id: node.id,
        name: node.title,
        description: node.description,
        descriptionHtml: node.descriptionHtml || null,
        // Search-engine title/description set in Shopify admin (may be empty)
        seoTitle: node.seo?.title || null,
        seoDescription: node.seo?.description || null,
        handle: node.handle,
        tags: node.tags,
        price: parseFloat(node.priceRange.minVariantPrice.amount),
        currency: node.priceRange.minVariantPrice.currencyCode,
        images: node.images.edges.map(({ node: img }) => ({
            url: img.url,
            altText: img.altText || node.title,
        })),
        image: node.images.edges[0]?.node.url || '/images/product-default.png',
        variantId: node.variants.edges[0]?.node.id,
        availableForSale: node.variants.edges[0]?.node.availableForSale,
        notes,
        topNotes,
        heartNotes,
        baseNotes,
    };
}

// Static site content

// Handle of the scent shown with a "Best Seller" badge across the site
export const BEST_SELLER_HANDLE = "geneva";

// Handles of scents shown with a "New" badge across the site and in the
// home page's New Releases section. Remove a handle once it's no longer new.
export const NEW_RELEASE_HANDLES = ['prague', 'sao-paulo'];

// Handles of scents shown with a "Low Stock" badge, mapped to units remaining
export const LOW_STOCK = {
    // Temporarily disabled; uncomment to restore stock badges and unit counts.
    // geneva: 4,
    // damascus: 6,
};

export const heroContent = {
    tagline: "Presence in Absence – Be Remembered",
    subtitle: "Signature Concentrated Perfume Oils"
};

export const designPrinciples = [
    {
        title: "Presence, Always",
        description: "A scent that announces you and lingers long after. Not a background note. A statement."
    },
    {
        title: "Enduring by Design",
        description: "Highly concentrated oils with depth, richness, and lasting power. Engineered to stay with you. Enduring by design."
    },
    {
        title: "Worn Daily",
        description: "Worn daily, not occasionally — as much a routine as it is a statement."
    }
];

export const teamContent = {
    heading: "Our Story",
    description: "It started simply enough. One of our co-founders brought back perfume oils from the local markets to his hometown and people couldn't get enough of it. The scents lasted long, they were unlike anything anyone had smelled before, and word spread fast. What began as a few samples turned into a steady stream of requests, and that's when the idea took shape — why not create something original? Something high end, long lasting and entirely our own.\n\nFrom there a second co-founder came on board. His passion for fragrance, business and building something meaningful made him the right person to have alongside. Together they started laying the foundations for what would become MAISON D'OUD, through plenty of bumps in the road and lessons learned along the way.\n\nAs the vision grew so did the team. A third member joined, bringing the energy and marketing instincts to push the brand further. His focus is making sure people don't just hear about MAISON D'OUD. They experience it.\n\nThree people. One shared obsession. Giving the world access to premium fragrance the way it was always meant to be worn.",
};

export const contactContent = {
    heading: "Get in touch",
    subtitle: "Whether you're looking for a signature scent or have questions about our collection, we're here to help.",
    email: "support@maisondoud.com",
    address: "Ireland"
};

export const partnersContent = {
    heading: "Our Standard",
    description: "Worn daily, carried always, remembered."
};
